import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Stack, Typography } from '@mui/material';

const COLLAPSED_ITEM_COUNT = 1;

type Section = { label: string; items: Array<string> };

type SectionedListProps = {
  sections: Array<Section>;
};

function CollapsibleSection({ section }: { section: Section }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMore = section.items.length > COLLAPSED_ITEM_COUNT;
  const visibleItems = isExpanded ? section.items : section.items.slice(0, COLLAPSED_ITEM_COUNT);

  const toggleButton = hasMore ? (
    <IconButton
      size="small"
      aria-label={isExpanded ? `Collapse ${section.label}` : `Expand ${section.label}`}
      onClick={() => setIsExpanded((expanded) => !expanded)}
      sx={{
        p: 0.25,
        ml: 0.25,
        verticalAlign: 'middle',
        transform: isExpanded ? 'rotate(180deg)' : 'none',
        transition: 'transform 0.15s',
      }}
    >
      <ExpandMoreIcon fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <Box>
      {isExpanded ? (
        <>
          <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {section.label}:
            </Box>
            {toggleButton}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {visibleItems.map((item) => (
              <Typography key={item} component="li" variant="body2">
                {item}
              </Typography>
            ))}
          </Box>
        </>
      ) : (
        <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
          <Box component="span" sx={{ fontWeight: 'bold', mr: 0.5 }}>
            {section.label}:
          </Box>
          {visibleItems[0]}
          {toggleButton}
        </Typography>
      )}
    </Box>
  );
}

export default function SectionedList({ sections }: SectionedListProps) {
  const nonEmptySections = sections.filter((section) => section.items.length > 0);

  if (nonEmptySections.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {nonEmptySections.map((section) => (
        <CollapsibleSection key={section.label} section={section} />
      ))}
    </Stack>
  );
}
